import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { usePaqueteDeLaEmpresa, useSesion } from '../../sesion/ProveedorDeSesion';
import { Marca } from '../Marca';
import { GRUPOS, INICIO, PLATAFORMA, seccionVisible, type Seccion } from './secciones';
import { Icono } from './iconos';

/** Se recuerda entre pantallas: quien la pliega la quiere plegada siempre, no una vez. */
const CLAVE_DEL_PLIEGUE = 'actualgold.menu.plegado';

/**
 * Navegación agrupada, sobre el negro de la marca.
 *
 * ## Las tres cosas que decide este archivo
 *
 * **Una sección que la persona no puede usar no se muestra**, y **un grupo que queda sin secciones
 * tampoco**. Sin esa segunda regla, una cajera con cinco permisos vería siete títulos de grupo con
 * nada adentro: un menú más grande que el de antes.
 *
 * Esconder no es la defensa —la API rechaza igual (INV-18)—: es cortesía. Un menú lleno de cosas
 * que dan error al tocarlas enseña a desconfiar de la pantalla.
 *
 * ## Por qué negra
 *
 * Porque es el negro del logotipo, el mismo del panel del acceso. Una barra oscura separa el
 * andamiaje del trabajo: lo blanco es donde se opera, lo negro es por dónde se llega. Y deja al
 * rojo de la marca hacer lo único que hace —marcar dónde estás— sin competir con ningún control.
 *
 * ## Por qué se pliega
 *
 * Con siete grupos y treinta pantallas, el menú compite por ancho con la tabla que alguien está
 * mirando. Plegado quedan los íconos, que es lo que usa quien ya sabe dónde está cada cosa.
 */
export function Navegacion(): React.ReactElement {
  const { t } = useTranslation();
  const { permisos, perfil } = useSesion();
  const rutaActual = useRouterState({ select: (estado) => estado.location.pathname });
  const [plegado, setPlegado] = useState(
    () => globalThis.localStorage?.getItem(CLAVE_DEL_PLIEGUE) === 'si',
  );

  const tieneFiscoElectronico = usePaqueteDeLaEmpresa()?.tieneFiscoElectronico ?? true;
  const puede = (seccion: Seccion): boolean =>
    seccionVisible(seccion, permisos, tieneFiscoElectronico);

  const estaActiva = (seccion: Seccion): boolean =>
    seccion.ruta === '/' ? rutaActual === '/' : rutaActual.startsWith(seccion.ruta);

  const enlace = (seccion: Seccion): React.ReactElement => (
    <Link
      key={seccion.ruta}
      to={seccion.ruta}
      aria-current={estaActiva(seccion) ? 'page' : undefined}
      title={plegado ? t(seccion.clave) : undefined}
      className={`flex items-center gap-2.5 border-l-2 py-1.5 text-sm transition-colors ${
        plegado ? 'justify-center px-2' : 'px-3'
      } ${
        estaActiva(seccion)
          ? // El filo rojo es lo único que hace el rojo de la marca acá: decir dónde estás.
            'border-marca bg-white/10 font-medium text-white'
          : 'border-transparent text-white/70 hover:bg-white/5 hover:text-white'
      }`}
    >
      <Icono nombre={seccion.icono} className="size-4 shrink-0" />
      {plegado ? <span className="sr-only">{t(seccion.clave)}</span> : t(seccion.clave)}
    </Link>
  );

  const gruposVisibles = GRUPOS.map((grupo) => ({
    clave: grupo.clave,
    secciones: grupo.secciones.filter(puede),
  })).filter((grupo) => grupo.secciones.length > 0);

  return (
    <div
      className={`flex h-full flex-col bg-marca-fondo ${plegado ? 'w-14' : 'w-52'} transition-[width]`}
    >
      <div
        className={`flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-3 ${
          plegado ? 'justify-center' : ''
        }`}
      >
        <Marca forma={plegado ? 'iso' : 'logotipo'} tinta="clara" className="h-5 w-auto" />
      </div>

      {/* **El scroll es de la lista, no de la página.** Con treinta pantallas el menú no entra en
          una ventana de portátil, y hasta acá se desplazaba la página entera: al bajar en una
          tabla larga el menú se iba de la vista. */}
      <nav
        aria-label={t('navegacion.titulo')}
        className="flex flex-1 flex-col gap-3 overflow-y-auto py-3"
      >
        {enlace(INICIO)}
        {gruposVisibles.map((grupo) => (
          <div key={grupo.clave} className="flex flex-col gap-0.5">
            {/* El título del grupo no es un enlace y no tiene que parecerlo: es una etiqueta.
                Plegado desaparece —una palabra cortada a catorce píxeles no es una etiqueta— y
                queda la separación, que es la que sigue agrupando. */}
            {plegado ? (
              <div className="mx-3 border-t border-white/10" />
            ) : (
              <p className="px-3 pb-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-white/40">
                {t(grupo.clave)}
              </p>
            )}
            {grupo.secciones.map(enlace)}
          </div>
        ))}
        {/* Al final y sola: no es de ningún comercio. La ve quien administra la instalación, que
            además puede ser miembro de un comercio, y entonces la tiene acá abajo. */}
        {perfil?.esAdministradorDePlataforma === true ? (
          <div className="flex flex-col gap-0.5">
            <div className="mx-3 border-t border-white/10" />
            {enlace(PLATAFORMA)}
          </div>
        ) : null}
      </nav>

      <button
        type="button"
        onClick={() => {
          const proximo = !plegado;
          setPlegado(proximo);
          globalThis.localStorage?.setItem(CLAVE_DEL_PLIEGUE, proximo ? 'si' : 'no');
        }}
        aria-label={plegado ? t('navegacion.desplegar') : t('navegacion.plegar')}
        className="flex shrink-0 items-center gap-2 border-t border-white/10 px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white"
      >
        {plegado ? (
          <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
        ) : (
          <PanelLeftClose className="size-4 shrink-0" aria-hidden />
        )}
        {plegado ? null : t('navegacion.plegar')}
      </button>
    </div>
  );
}
